import React, { useCallback, useContext, useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { Avatar, Button, Paper, TextField, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { alertSuccess } from "components/Alert"

const initForm = { SiteCd: "", ScAbbr: "", FName: "", LName: "", Tel: "", LicNm: "" }

/**
 * /liff/register — first (and normally only) visit. The driver types the site code and the
 * subcontractor abbreviation the operator gave them; the server checks both exist and pins
 * this LINE account to that subcontractor. Re-opening later pre-fills the form for edits.
 */
const LiffRegister = ({ liff }) => {
  const { ax, isLoading } = useContext(GlobalStateContext)
  const history = useHistory()
  const { profile, idToken, reloadProfile } = liff
  const [form, setForm] = useState(initForm)

  useEffect(() => {
    if (!profile?.IsReg) return
    setForm({
      SiteCd: profile.SiteCd || "", ScAbbr: profile.ScAbbr || "",
      FName: profile.FName || "", LName: profile.LName || "",
      Tel: profile.Tel || "", LicNm: profile.LicNm || "",
    })
  }, [profile])

  const field = useCallback((name, label, extra = {}) => ({
    label, size: "small", fullWidth: true, required: true,
    value: form[name],
    onChange: (e) => setForm(o => ({ ...o, [name]: e.target.value })),
    ...extra,
  }), [form])

  const handleSubmit = useCallback((e) => {
    e.preventDefault()
    ax.post("/no-session/line-sub/registerLineDriver", { ...form, IdToken: idToken }).then(async (res) => {
      if (!res.data) return
      await reloadProfile()
      alertSuccess(`ลงทะเบียนกับ ${res.data.ScNm} (${res.data.SiteSNm}) เรียบร้อย`)
      history.replace("/liff/upload")
    })
  }, [ax, form, idToken, reloadProfile, history])

  return (
    <BoxFC p={2} gap={2} maxWidth={480} mx="auto">
      <BoxFR gap={1.5} alignItems="center">
        <Avatar src={profile?.PicUrl || undefined} sx={{ width: 48, height: 48 }} />
        <BoxFC gap={0}>
          <Typography fontWeight="bold">{profile?.DispNm || "-"}</Typography>
          <Typography variant="body2" color="text.secondary">
            {profile?.IsReg ? `ลงทะเบียนแล้ว: ${profile.ScNm} (${profile.SiteSNm})` : "ยังไม่ได้ลงทะเบียน"}
          </Typography>
        </BoxFC>
      </BoxFR>

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
        <BoxFC gap={2}>
          <Typography variant="h6">ลงทะเบียนคนขับรถร่วม</Typography>
          <Typography variant="body2" color="text.secondary">
            กรอกรหัสบริษัทและตัวย่อรถร่วมที่ได้รับจากเจ้าหน้าที่
          </Typography>
          <TextField {...field("SiteCd", "รหัสบริษัท (Site Code)", { autoCapitalize: "none", autoComplete: "off" })} />
          <TextField {...field("ScAbbr", "ตัวย่อรถร่วม", { autoCapitalize: "characters", autoComplete: "off" })} />
          <BoxFR gap={1}>
            <TextField {...field("FName", "ชื่อ")} />
            <TextField {...field("LName", "นามสกุล")} />
          </BoxFR>
          <TextField {...field("Tel", "เบอร์โทร", { type: "tel", inputMode: "tel" })} />
          <TextField {...field("LicNm", "ทะเบียนรถ")} />
          <Button type="submit" variant="contained" size="large" disabled={isLoading}>
            {profile?.IsReg ? "บันทึกการแก้ไข" : "ลงทะเบียน"}
          </Button>
          {profile?.IsReg && (
            <Button variant="text" onClick={() => history.replace("/liff/upload")}>ไปหน้าอัปโหลด EIR</Button>
          )}
        </BoxFC>
      </Paper>
    </BoxFC>
  )
}

export default LiffRegister
